import { useState } from 'react';
import {
  Bookmark,
  Search as SearchIcon,
  Loader2,
  Linkedin,
  ExternalLink,
  Trash2,
  User,
  Sparkles,
  ChevronDown,
  ChevronUp,
  MessageSquare,
  ThumbsUp,
  X,
  RefreshCw,
} from 'lucide-react';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { useUiStore } from '../../store/ui';
import { useSavedItems, useDeleteSavedItem, type SavedItem, type SavedItemKind } from '../../hooks/useSavedItems';
import { HelpHint } from '../ui/HelpHint';
import { Tooltip } from '../ui/Tooltip';

// Filtri per tipo di elemento (chip in alto). 'all' = nessun filtro.
const KIND_FILTERS: { key: SavedItemKind | 'all'; label: string }[] = [
  { key: 'all', label: 'Tutti' },
  { key: 'post', label: 'Post' },
  { key: 'profile', label: 'Profili' },
  { key: 'engagement', label: 'Engagement' },
  { key: 'dm', label: 'Messaggi' },
];

/**
 * Vista Salvati (CRM): elementi salvati da LinkedIn tramite l'estensione Chrome.
 * Grafica IDENTICA alla vista Posta (tema .wm-list: palette viola chiara,
 * chip filtri, tabella con intestazione viola, pannello dettaglio chiaro).
 * Il contenuto vive in SavedItemsContent, riusato dal tab "Salvati" di LinkedInView.
 */
export function SavedItemsView() {
  return (
    <div className="flex flex-1 overflow-hidden">
      <SavedItemsContent standalone />
    </div>
  );
}

// Contenuto della vista Salvati (senza sidebar): riusabile dentro LinkedInView.
// standalone=true quando è la vista a sé (aggiunge wrapper .wm-list + header proprio).
export function SavedItemsContent({ standalone = false }: { standalone?: boolean }) {
  const [kind, setKind] = useState<SavedItemKind | 'all'>('all');
  const [query, setQuery] = useState('');
  // Elemento selezionato: null = solo elenco (default), altrimenti dettaglio a destra.
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const { data: items, isLoading, refetch, isFetching } = useSavedItems({
    kind: kind === 'all' ? undefined : kind,
    q: query.trim() || undefined,
  });

  return (
    <section className={(standalone ? 'wm-list ' : '') + 'flex flex-1 flex-col overflow-hidden'}>
      {/* Barra superiore: titolo + ricerca (stessa grafica del Cruscotto posta) */}
      <div className="shrink-0 border-b px-5 pb-3 pt-4" style={{ borderColor: 'var(--border)' }}>
        <div className="flex items-center gap-2">
          <Bookmark size={16} style={{ color: 'var(--accent)' }} />
          <h1 className="text-[15px] font-semibold" style={{ color: 'var(--accent-dark)' }}>
            Salvati da LinkedIn
          </h1>
          {items && (
            <span className="text-xs" style={{ color: 'var(--muted)' }}>
              · {items.length}
            </span>
          )}
          {/* Aiuto contestuale della sezione Salvati */}
          <HelpHint topic="saved" />
          {/* Pulsante Aggiorna: ricarica manualmente l'elenco degli elementi salvati */}
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            title="Aggiorna elenco"
            className="wm-btn-ghost ml-auto flex items-center gap-1.5 rounded-md px-2.5 py-1 text-[11px] font-medium transition disabled:opacity-50"
          >
            <RefreshCw size={12} className={isFetching ? 'animate-spin' : ''} />
            Aggiorna
          </button>
          <div className="relative">
            <SearchIcon
              size={13}
              className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2"
              style={{ color: 'var(--muted)' }}
            />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Cerca nei contenuti salvati…"
              className="wm-search"
              aria-label="Cerca nei contenuti salvati"
            />
            {query && (
              <button
                className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-0.5"
                style={{ color: 'var(--muted)' }}
                onClick={() => setQuery('')}
                title="Cancella ricerca"
              >
                <X size={13} />
              </button>
            )}
          </div>
        </div>

        {/* Chip filtri per tipo (stessa grafica dei filtri rapidi della Posta) */}
        <div className="mt-3 flex flex-wrap items-center gap-1.5">
          {KIND_FILTERS.map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setKind(key)}
              className={'wm-chip px-2.5 py-1 text-[11px] font-medium' + (kind === key ? ' is-active' : '')}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* ELENCO a tabella (stile lista mail) + dettaglio a destra al click */}
      <div className="flex flex-1 overflow-hidden">
        <div className="flex-1 overflow-y-auto px-4 pb-4 pt-3">
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 size={16} className="animate-spin" style={{ color: 'var(--muted)' }} />
            </div>
          ) : !items || items.length === 0 ? (
            <SavedEmptyState filtered={kind !== 'all' || query.trim().length > 0} />
          ) : (
            <table className="wm-table w-full border-separate" style={{ borderSpacing: 0 }}>
              <thead>
                <tr>
                  <th style={{ width: 90 }}>Data</th>
                  <th style={{ width: 180 }}>Autore</th>
                  <th>Titolo</th>
                  <th style={{ width: 260 }}>Tipo / Tag</th>
                  <th style={{ width: 110 }}>Azioni</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <SavedItemRow
                    key={item.id}
                    item={item}
                    selected={item.id === selectedId}
                    onSelect={() => setSelectedId(item.id === selectedId ? null : item.id)}
                  />
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Pannello dettaglio (compare solo dopo il click su una riga) */}
        {selectedId && items && (
          (() => {
            const sel = items.find((i) => i.id === selectedId);
            if (!sel) return null;
            return (
              <aside
                className="w-[420px] shrink-0 overflow-y-auto border-l"
                style={{ borderColor: 'var(--border)', background: 'var(--card)' }}
              >
                <SavedItemDetail item={sel} onClose={() => setSelectedId(null)} />
              </aside>
            );
          })()
        )}
      </div>
    </section>
  );
}

// ---------- Riga di tabella (stile lista mail): data, autore, titolo, tipo+tag, azioni ----------
function SavedItemRow({ item, selected, onSelect }: { item: SavedItem; selected: boolean; onSelect: () => void }) {
  const { label } = kindMeta(item.kind);
  const remove = useDeleteSavedItem();

  const onDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm('Eliminare questo elemento salvato?')) return;
    remove.mutate(item.id);
  };

  return (
    <tr className={'wm-row' + (selected ? ' is-active' : '')} onClick={onSelect}>
      <td className="whitespace-nowrap" style={{ color: 'var(--muted)' }}>
        {format(new Date(item.created_at), 'd MMM', { locale: it })}
      </td>
      <td className="max-w-0 truncate">{item.author_name || '—'}</td>
      <td className="max-w-0 truncate font-medium">{item.title?.trim() || '(senza titolo)'}</td>
      <td>
        <span className="flex flex-wrap items-center gap-1">
          <span className="wm-badge wm-badge-cat">{label}</span>
          {item.ai_tags.slice(0, 3).map((t) => (
            <span
              key={t}
              className="wm-badge"
              style={{ background: 'var(--surface-mid)', color: 'var(--text)' }}
            >
              {t}
            </span>
          ))}
        </span>
      </td>
      <td onClick={(e) => e.stopPropagation()}>
        <span className="flex items-center gap-1">
          {item.author_linkedin_url && (
            <Tooltip label="Apri profilo autore su LinkedIn">
              <a
                href={item.author_linkedin_url}
                target="_blank"
                rel="noreferrer"
                title="Apri profilo autore su LinkedIn"
                className="wm-btn px-2 py-1"
              >
                <Linkedin size={12} />
              </a>
            </Tooltip>
          )}
          {item.url && (
            <Tooltip label="Apri l'originale su LinkedIn">
              <a
                href={item.url}
                target="_blank"
                rel="noreferrer"
                title="Apri l'originale su LinkedIn"
                className="wm-btn px-2 py-1"
              >
                <ExternalLink size={12} />
              </a>
            </Tooltip>
          )}
          <Tooltip label="Elimina elemento">
            <button
              onClick={onDelete}
              disabled={remove.isPending}
              title="Elimina elemento"
              className="wm-btn px-2 py-1 disabled:opacity-50"
            >
              {remove.isPending ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
            </button>
          </Tooltip>
        </span>
      </td>
    </tr>
  );
}

// Empty state: invita a installare l'estensione Chrome (CTA → tab Estensione)
function SavedEmptyState({ filtered }: { filtered: boolean }) {
  const openSettings = useUiStore((s) => s.openSettings);
  if (filtered) {
    return (
      <div className="py-16 text-center text-xs" style={{ color: 'var(--muted)' }}>
        Nessun elemento salvato corrisponde ai filtri.
      </div>
    );
  }
  return (
    <div
      className="mx-auto mt-12 flex max-w-sm flex-col items-center gap-3 rounded-xl border border-dashed px-6 py-12 text-center"
      style={{ borderColor: 'var(--border)', background: 'var(--card)' }}
    >
      <Linkedin size={26} style={{ color: 'var(--accent)' }} strokeWidth={1.5} />
      <p className="text-sm font-medium" style={{ color: 'var(--text)' }}>
        Nessun elemento salvato
      </p>
      <p className="text-xs leading-relaxed" style={{ color: 'var(--muted)' }}>
        Installa l&rsquo;estensione Chrome per iniziare a salvare da LinkedIn: post, profili,
        engagement e messaggi arrivano qui, già riassunti e taggati dall&rsquo;AI.
      </p>
      <button onClick={() => openSettings('extension')} className="wm-btn wm-btn-primary mt-1">
        Configura l&rsquo;estensione
      </button>
    </div>
  );
}

// Icona + etichetta per il tipo di elemento salvato
function kindMeta(kind: SavedItemKind) {
  switch (kind) {
    case 'post':
      return { label: 'Post', Icon: Bookmark };
    case 'profile':
      return { label: 'Profilo', Icon: User };
    case 'engagement':
      return { label: 'Engagement', Icon: ThumbsUp };
    case 'dm':
      return { label: 'Messaggio', Icon: MessageSquare };
    default:
      return { label: 'Elemento', Icon: Bookmark };
  }
}

// ---------- Pannello dettaglio dell'elemento selezionato (tema chiaro wm-list) ----------
function SavedItemDetail({ item, onClose }: { item: SavedItem; onClose: () => void }) {
  const [expanded, setExpanded] = useState(false);
  const remove = useDeleteSavedItem();
  const setSelectedContact = useUiStore((s) => s.setSelectedContact);
  const setCurrentView = useUiStore((s) => s.setCurrentView);
  const { label, Icon } = kindMeta(item.kind);

  // Naviga alla scheda del contatto collegato nella vista Contatti
  const openContact = () => {
    if (!item.contact_id) return;
    setSelectedContact(item.contact_id);
    setCurrentView('contacts');
  };

  const onDelete = () => {
    if (!window.confirm('Eliminare questo elemento salvato?')) return;
    remove.mutate(item.id);
  };

  return (
    <div className="flex flex-col p-4">
      {/* Testata: tipo + data + azioni */}
      <div className="mb-2 flex items-center gap-2">
        <span className="wm-badge wm-badge-cat">
          <Icon size={10} /> {label}
        </span>
        <span className="text-[10px]" style={{ color: 'var(--muted)' }}>
          {format(new Date(item.created_at), 'd MMM yyyy', { locale: it })}
        </span>
        <div className="ml-auto flex items-center gap-1">
          {item.url && (
            <Tooltip label="Apri l'originale su LinkedIn">
              <a
                href={item.url}
                target="_blank"
                rel="noreferrer"
                title="Apri l'originale su LinkedIn"
                className="wm-btn px-2 py-1"
              >
                <ExternalLink size={12} />
              </a>
            </Tooltip>
          )}
          <Tooltip label="Elimina elemento">
            <button
              onClick={onDelete}
              disabled={remove.isPending}
              title="Elimina elemento"
              className="wm-btn px-2 py-1 disabled:opacity-50"
            >
              {remove.isPending ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
            </button>
          </Tooltip>
          <Tooltip label="Chiudi dettaglio">
            <button onClick={onClose} title="Chiudi dettaglio" className="wm-btn px-2 py-1">
              <X size={12} />
            </button>
          </Tooltip>
        </div>
      </div>

      {/* Titolo + autore */}
      <h3 className="text-sm font-semibold leading-snug" style={{ color: 'var(--text)' }}>
        {item.title?.trim() || '(senza titolo)'}
      </h3>
      {item.author_name && (
        <div className="mt-0.5 text-[11px]" style={{ color: 'var(--muted)' }}>
          {item.author_linkedin_url ? (
            <a
              href={item.author_linkedin_url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 transition hover:underline"
              style={{ color: 'var(--accent)' }}
            >
              <Linkedin size={10} /> {item.author_name}
            </a>
          ) : (
            item.author_name
          )}
        </div>
      )}

      {/* Estratto contenuto */}
      {item.content && (
        <p className="mt-1.5 whitespace-pre-wrap text-[12px] leading-relaxed" style={{ color: 'var(--text)' }}>
          {item.content}
        </p>
      )}

      {/* Tag AI */}
      {item.ai_tags.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {item.ai_tags.map((t) => (
            <span
              key={t}
              className="wm-badge"
              style={{ background: 'var(--surface-mid)', color: 'var(--text)' }}
            >
              {t}
            </span>
          ))}
        </div>
      )}

      {/* Riassunto AI espandibile */}
      {item.ai_summary && (
        <div className="mt-2">
          <button
            onClick={() => setExpanded((v) => !v)}
            className="flex items-center gap-1 text-[11px] font-medium transition"
            style={{ color: 'var(--accent)' }}
          >
            <Sparkles size={11} />
            {expanded ? 'Nascondi riassunto AI' : 'Mostra riassunto AI'}
            {expanded ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
          </button>
          {expanded && (
            <p
              className="mt-1.5 rounded-md border p-2 text-[11px] leading-relaxed"
              style={{ borderColor: 'var(--accent-light)', background: 'var(--accent-soft)', color: 'var(--text)' }}
            >
              {item.ai_summary}
            </p>
          )}
        </div>
      )}

      {/* Piede: link al contatto collegato */}
      {item.contact_id && (
        <div className="mt-auto pt-2.5">
          <button
            onClick={openContact}
            className="flex items-center gap-1.5 text-[11px] font-medium transition hover:underline"
            style={{ color: 'var(--accent)' }}
            title="Apri la scheda del contatto collegato"
          >
            <User size={11} /> Apri contatto
          </button>
        </div>
      )}
    </div>
  );
}
